import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'

import en from './locales/en.json'
import es from './locales/es.json'
import pt from './locales/pt.json'
import zhCN from './locales/zh-CN.json'
import { googleTranslate } from './utils/googleTranslate'
import { getCache, setCache } from './utils/translationCache'

export const APP_LANGUAGE_STORAGE_KEY = 'app_language'
export const SUPPORTED_LANGUAGES = ['en', 'es', 'pt', 'zh-CN'] as const
export const DEFAULT_LANGUAGE = 'en'

export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number]

export const isSupportedLanguage = (value: string | null | undefined): value is SupportedLanguage => !!value && SUPPORTED_LANGUAGES.includes(value as SupportedLanguage)

export const normalizeLanguage = (value: string | null | undefined): SupportedLanguage => (isSupportedLanguage(value) ? value : DEFAULT_LANGUAGE)

export const getInitialLanguage = (): SupportedLanguage => {
    if (process.env.NODE_ENV === 'test') return DEFAULT_LANGUAGE
    if (globalThis.window === undefined) return DEFAULT_LANGUAGE
    const savedLanguage = localStorage.getItem(APP_LANGUAGE_STORAGE_KEY)
    const initialLanguage = normalizeLanguage(savedLanguage)

    if (savedLanguage !== initialLanguage) {
        localStorage.setItem(APP_LANGUAGE_STORAGE_KEY, initialLanguage)
    }

    return initialLanguage
}

const inFlightKeys = new Set<string>()

const translateMissingKey = async (lng: string, ns: string, key: string, fallbackValue: string): Promise<void> => {
    if (lng === DEFAULT_LANGUAGE) return

    const flightId = `${lng}:${ns}:${key}`
    if (inFlightKeys.has(flightId)) return

    const cached = getCache(lng, key)
    if (cached) {
        i18n.addResource(lng, ns, key, cached)
        return
    }

    inFlightKeys.add(flightId)
    try {
        const sourceText = fallbackValue || key
        const translated = await googleTranslate(sourceText, lng)
        if (translated && translated !== sourceText) {
            setCache(lng, key, translated)
            i18n.addResource(lng, ns, key, translated)
        }
    } catch {
        // silently fall back to the original key
    } finally {
        inFlightKeys.delete(flightId)
    }
}

const missingKeyHandler = (lngs: readonly string[], ns: string, key: string, fallbackValue: string): void => {
    // translateMissingKey handles its own errors, so this never rejects.
    void Promise.all(lngs.map((lng) => translateMissingKey(lng, ns, key, fallbackValue)))
}

i18n.use(initReactI18next)
    .init({
        resources: {
            en: { translation: en },
            es: { translation: es },
            pt: { translation: pt },
            'zh-CN': { translation: zhCN },
        },
        lng: getInitialLanguage(),
        supportedLngs: SUPPORTED_LANGUAGES,
        fallbackLng: DEFAULT_LANGUAGE,
        nsSeparator: false,
        saveMissing: true,
        missingKeyHandler,
        interpolation: {
            escapeValue: false,
        },
    })
    .catch((error) => {
        console.error('i18n initialization failed:', error)
    })

export default i18n
